import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../../lib/spformsApi';

const {
  FiX,
  FiPlus,
  FiTrash2,
  FiSave,
  FiTag,
  FiLoader,
} = FiIcons;

const DISCOUNT_TYPES = [
  { value: 'fixed', label: 'Subtract Amount' },
  { value: 'final_price', label: 'Set Final Price' },
];

function emptyCode({ tenantId, groupId }) {
  return {
    id: null,
    tenant_id: tenantId,
    intake_group_id: groupId,
    code: '',
    discount_type: 'fixed',
    amount: 0,
    active: true,
    notes: '',
  };
}

export default function DiscountCodesModal({ groupId, tenantId, onClose }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCodes = async () => {
    if (!tenantId || !groupId) return;

    try {
      setLoading(true);
      setErrorMessage('');

      const data = await spformsApi.discountCodesList(tenantId, groupId);
      setCodes(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, [tenantId, groupId]);

  const addCode = () => {
    setCodes((prev) => [
      ...prev,
      emptyCode({
        tenantId,
        groupId,
      }),
    ]);
  };

  const updateCodeState = (index, updates) => {
    setCodes((prev) => {
      const next = [...prev];

      next[index] = {
        ...next[index],
        ...updates,
      };

      return next;
    });
  };

  const removeCode = async (index) => {
    const code = codes[index];

    if (!code) return;

    if (code.id) {
      const confirmed = window.confirm('Delete this discount code permanently?');
      if (!confirmed) return;

      try {
        await spformsApi.discountCodeDelete(tenantId, code.id);
      } catch (error) {
        setErrorMessage(error.message || 'Failed to delete discount code');
        return;
      }
    }

    setCodes((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage('');

      for (const code of codes) {
        const payload = {
          ...code,
          tenant_id: tenantId,
          intake_group_id: groupId,
          code: String(code.code || '').trim().toUpperCase(),
          discount_type: code.discount_type || 'fixed',
          amount: Number(code.amount || 0),
          active: code.active !== false,
          notes: code.notes || null,
        };

        if (!payload.code) continue;

        if (payload.id) {
          await spformsApi.discountCodeUpdate(payload);
        } else {
          await spformsApi.discountCodeCreate(payload);
        }
      }

      onClose();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save discount codes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-8 border-b bg-gray-50/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-lg shadow-emerald-100">
              <SafeIcon icon={FiTag} size={24} />
            </div>

            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Discount Codes
              </h2>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
                Event-specific promo codes and final-price codes
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {errorMessage ? (
          <div className="mx-8 mt-6 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
            {errorMessage}
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto p-8 space-y-4 bg-gray-50/30">
          {loading ? (
            <div className="py-20 text-center animate-pulse text-gray-400 font-black uppercase tracking-[0.3em] text-xs">
              Loading discount codes...
            </div>
          ) : (
            <>
              {codes.length === 0 ? (
                <div className="rounded-[2rem] border-2 border-dashed border-gray-200 bg-white p-12 text-center">
                  <div className="text-lg font-black text-gray-900">
                    No discount codes yet
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-400">
                    Add a code like EARLY20, FREEPLAYER, or SIBLING10.
                  </p>
                </div>
              ) : null}

              {codes.map((code, index) => (
                <div
                  key={code.id || index}
                  className="p-6 border-2 border-gray-100 rounded-[2rem] bg-white shadow-sm"
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-end">
                    <div className="md:col-span-3 space-y-2">
                      <label className="label-small">Code</label>
                      <input
                        value={code.code || ''}
                        onChange={(e) =>
                          updateCodeState(index, {
                            code: e.target.value.toUpperCase(),
                          })
                        }
                        className="field-input font-black uppercase tracking-widest"
                        placeholder="SUMMER20"
                      />
                    </div>

                    <div className="md:col-span-3 space-y-2">
                      <label className="label-small">Type</label>
                      <select
                        value={code.discount_type || 'fixed'}
                        onChange={(e) =>
                          updateCodeState(index, {
                            discount_type: e.target.value,
                          })
                        }
                        className="field-input"
                      >
                        {DISCOUNT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 space-y-2">
                      <label className="label-small">
                        {code.discount_type === 'final_price'
                          ? 'Final Price (€)'
                          : 'Discount (€)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={code.amount ?? 0}
                        onChange={(e) =>
                          updateCodeState(index, {
                            amount: e.target.value,
                          })
                        }
                        className="field-input"
                      />
                    </div>

                    <div className="md:col-span-2 flex items-center gap-3 pb-4">
                      <input
                        type="checkbox"
                        checked={code.active !== false}
                        onChange={(e) =>
                          updateCodeState(index, {
                            active: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-2 border-gray-300 text-emerald-600"
                      />
                      <span className="text-xs font-black text-gray-600 uppercase tracking-widest">
                        Active
                      </span>
                    </div>

                    <div className="md:col-span-2 flex justify-end pb-2">
                      <button
                        type="button"
                        onClick={() => removeCode(index)}
                        className="w-11 h-11 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>

                    <div className="md:col-span-12 space-y-2">
                      <label className="label-small">Notes</label>
                      <input
                        value={code.notes || ''}
                        onChange={(e) =>
                          updateCodeState(index, {
                            notes: e.target.value,
                          })
                        }
                        className="field-input"
                        placeholder="Optional internal note"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCode}
                className="w-full py-8 border-2 border-dashed border-gray-200 rounded-[2rem] flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <SafeIcon icon={FiPlus} />
                </div>

                <span className="text-xs font-black uppercase tracking-[0.2em]">
                  Add Discount Code
                </span>
              </button>
            </>
          )}
        </div>

        <div className="p-8 border-t bg-gray-50 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-4 text-xs font-black text-gray-500 uppercase tracking-widest hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-12 py-4 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center gap-3 disabled:opacity-60"
          >
            {saving ? (
              <SafeIcon icon={FiLoader} className="animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} />
            )}
            Save Discount Codes
          </button>
        </div>

        <style>{`
          .label-small {
            font-size: 10px;
            font-weight: 900;
            color: #9ca3af;
            text-transform: uppercase;
            letter-spacing: 0.2em;
          }
          .field-input {
            width: 100%;
            padding: 0.85rem 1.25rem;
            border-radius: 1rem;
            border: 2px solid #f3f4f6;
            font-size: 0.875rem;
            font-weight: 700;
            background: #fff;
            outline: none;
            transition: border-color 0.2s;
          }
          .field-input:focus {
            border-color: #10b981;
          }
        `}</style>
      </div>
    </div>
  );
}